/**
 * Small motion hooks for the Insights v2 surface.
 *
 * - useCountUp      — RAF-driven number tween (counts from 0 to `value`).
 * - useInView       — IntersectionObserver wrapper, triggers once.
 * - useReducedMotion — mirrors the `prefers-reduced-motion` media query.
 * - useMousePosition — ref + {x, y} relative to a target element.
 *
 * No external dependencies. All hooks are SSR-safe (return neutral
 * defaults on the server, real values on the client).
 */
import { useEffect, useRef, useState } from 'react';

/**
 * useCountUp — tween a numeric value from 0 → `value` over `duration` ms.
 * Returns the in-flight value to render. Pauses if `start` is false so
 * the page can reveal numbers only after the hero is on screen.
 */
export function useCountUp(value, { duration = 1400, start = true, decimals = 0 } = {}) {
  const [display, setDisplay] = useState(start ? 0 : value);
  const fromRef = useRef(start ? 0 : value);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) {
      setDisplay(value);
      return undefined;
    }
    const from = fromRef.current;
    const to = Number(value) || 0;
    if (from === to) {
      setDisplay(to);
      return undefined;
    }
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // cubic-out
    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = ease(t);
      const v = from + (to - from) * eased;
      setDisplay(decimals > 0 ? Number(v.toFixed(decimals)) : Math.round(v));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, start, decimals]);

  return display;
}

/**
 * useInView — returns a ref + a boolean that flips true once the
 * observed element enters the viewport (default 12% threshold).
 * `once: true` (default) keeps the boolean true after the first hit.
 */
export function useInView({ threshold = 0.12, once = true, rootMargin = '0px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once, rootMargin]);

  return [ref, inView];
}

/**
 * useReducedMotion — true when the user has prefers-reduced-motion: reduce.
 * Used to short-circuit the count-up + breath effects for accessibility.
 */
export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

/**
 * useMousePosition — tracks the mouse position normalised to [-1, 1]
 * over the ref'd element. Used by the ambient aurora. Re-renders only
 * when the position changes enough to matter (>0.02 of viewport).
 */
export function useMousePosition() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0, active: false });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const node = ref.current;
    if (!node) return undefined;

    let lastX = 0;
    let lastY = 0;
    let raf = null;

    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const rect = node.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        // Snap to 2 decimals so we don't re-render on every micro-jitter
        const nx = Math.max(-1, Math.min(1, Number(x.toFixed(2))));
        const ny = Math.max(-1, Math.min(1, Number(y.toFixed(2))));
        if (Math.abs(nx - lastX) > 0.04 || Math.abs(ny - lastY) > 0.04) {
          lastX = nx;
          lastY = ny;
          setPos({ x: nx, y: ny, active: true });
        }
      });
    };

    const onLeave = () => setPos((p) => ({ ...p, active: false }));

    node.addEventListener('mousemove', onMove);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.removeEventListener('mousemove', onMove);
      node.removeEventListener('mouseleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return [ref, pos];
}
